import { useAuth0 } from '@auth0/auth0-react';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { AccessBlockedPage } from '@/components/ui/AccessBlockedPage';
import { useAppSelector } from '@/store';
import { useAuthorization } from './useAuthorization';

interface AuthGuardProps {
  children: React.ReactNode;
  /** Admin (platform-wide or their own tenant) — see useAuthorization's canManageAccess. */
  requiredManageAccess?: boolean;
  /** 'all' — tenant-less/platform-scope identity, any role (Partners, full Customers list). See useAuthorization's scope. */
  requiredScope?: 'all';
  /** Tenant-less Admin only — platform-wide surfaces (Infrastructure, Platform Users, System Roles) with no tenant-scoped equivalent. See useAuthorization's isPlatformAdmin. */
  requiredPlatformAdmin?: boolean;
  /** If 'partner', child-tenant users are denied; a tenant-less or partner Admin passes. */
  requiredTenantType?: 'partner';
  /** Permission string (e.g. "view:release") — for a route that's permission-shaped (SUN-408 Phase 6 addendum). */
  requiredPermission?: string;
  fallbackPath?: string;
}

export const AuthGuard = ({
  children,
  requiredManageAccess,
  requiredScope,
  requiredPlatformAdmin,
  requiredTenantType,
  requiredPermission,
  fallbackPath = '/',
}: AuthGuardProps) => {
  if (import.meta.env.VITE_SKIP_AUTH === 'true') {
    return <>{children}</>;
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR-safe early return before hooks — this component is only rendered on the client
  const { isLoading, isAuthenticated: auth0IsAuthenticated, loginWithRedirect } = useAuth0();
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR-safe early return before hooks — this component is only rendered on the client
  const authz = useAuthorization();
  const {
    hasPermission,
    canManageAccess,
    isPlatformAdmin,
    scope,
    tenantId,
    isIdentityLoading,
    identityError,
  } = authz;
  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR-safe early return before hooks — this component is only rendered on the client
  const location = useLocation();

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR-safe early return before hooks — this component is only rendered on the client
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  // biome-ignore lint/correctness/useHookAtTopLevel: Intentional SSR-safe early return before hooks — this component is only rendered on the client
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !auth0IsAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: location.pathname + location.search },
      });
    }
  }, [
    isLoading,
    isAuthenticated,
    auth0IsAuthenticated,
    loginWithRedirect,
    location.pathname,
    location.search,
  ]);

  if (isLoading || isIdentityLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Redirecting to login...</p>
      </div>
    );
  }

  // No sunstone.users row for this identity — a valid Auth0 login with
  // nothing provisioned in the DB. Must deny explicitly here: without this,
  // every list RPC just returns an empty-but-successful result and the app
  // renders as an ordinary, confusing "no data" state instead of denying access.
  if (identityError) {
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="Your account isn't set up yet."
        detail="Contact an administrator to get access."
        action={{ type: 'logout' }}
      />
    );
  }

  // Manage-access check: Admin, platform-wide or their own tenant
  if (requiredManageAccess && !canManageAccess) {
    const landingPath = tenantId ? `/tenants/${tenantId}` : fallbackPath;
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this page."
        detail="Required: Admin"
        action={{ type: 'link', label: 'Go to your dashboard', to: landingPath }}
      />
    );
  }

  // Scope check: tenant-less/platform-scope identity, any role (Partners,
  // full Customers list)
  if (requiredScope === 'all' && scope !== 'all') {
    const landingPath = tenantId ? `/tenants/${tenantId}` : fallbackPath;
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this page."
        detail="Required: platform-wide access"
        action={{ type: 'link', label: 'Go to your dashboard', to: landingPath }}
      />
    );
  }

  // Platform-admin check: platform-wide user/role management (Platform Users, System Roles)
  if (requiredPlatformAdmin && !isPlatformAdmin) {
    const landingPath = tenantId ? `/tenants/${tenantId}` : fallbackPath;
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this page."
        detail="Required: tenant-less Admin"
        action={{ type: 'link', label: 'Go to your dashboard', to: landingPath }}
      />
    );
  }

  // Tenant type check: deny child-tenant scope; partner and tenant-less
  // (platform) scope pass through
  if (requiredTenantType === 'partner' && scope === 'tenant') {
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this page."
        action={{ type: 'link', label: 'Go to your dashboard', to: `/tenants/${tenantId}` }}
      />
    );
  }

  // Permission check (SUN-408 Phase 6 addendum): a restricted user with the
  // required coarse role but not the specific permission (e.g. a User-role
  // holder whose default permission set lacks view:release) is denied here,
  // instead of reaching the page and getting an empty list or a raw error.
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <AccessBlockedPage
        title="Access Denied"
        message="You don't have permission to access this page."
        detail={`Required permission: ${requiredPermission}`}
        action={{
          type: 'link',
          label: 'Go to your dashboard',
          to: tenantId ? `/tenants/${tenantId}` : fallbackPath,
        }}
      />
    );
  }

  return <>{children}</>;
};
